import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ContentCopyOutlinedIcon, DeleteOutlineOutlinedIcon, EditOutlinedIcon } from 'tuui';
import { isNetworkAccountType } from 'modules/account-management';
import { useDeleteCampaignGroup } from 'modules/campaigns/hooks/useDeleteCampaignGroup';
import { useCampaignGroupEnabled } from 'modules/campaigns/modules/campaigns-group-form/hooks/useCampaignGroupEnabled';
import { useOpenCampaignsGroupDuplicate } from 'modules/campaigns/modules/campaigns-group/hooks/useOpenCampaignsGroupDuplicate';
import useOpenCampaignsGroupEditor from 'modules/campaigns/modules/campaigns-group/hooks/useOpenCampaignsGroupEditor';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { selectedAccountSelector } from 'selectors';
import { isCampaignGroupDeleted } from '../../config/predicates/campaignGroupPredicates';
import ActionsCellRenderer from './ActionsCellRenderer';

const CampaignsGroupEditTooltipContent = ({ isDisabled }) =>
    isDisabled ? (
        <FormattedMessage
            id="app.campaigns.editCampaignsGroup.disabled"
            defaultMessage="Editing Campaign group from the network level is not supported yet"
        />
    ) : (
        <FormattedMessage id="app.campaigns.editCampaignsGroup" defaultMessage="Edit Group" />
    );

const CampaignsGroupActionsCellRenderer = ({ data, context, ...rest }) => {
    const [isDeleteDisabled, setIsDeleteDisabled] = useState(isCampaignGroupDeleted(data));
    const openCampaignsGroupEditor = useOpenCampaignsGroupEditor(data);
    const openCampaignsGroupDuplicate = useOpenCampaignsGroupDuplicate(data);
    const { type } = useSelector(selectedAccountSelector);
    const isDisabled = context?.userPermissions && isNetworkAccountType(type);
    const isCampaignGroupExtensionEnabled = useCampaignGroupEnabled();
    const deleteCampaignGroup = useDeleteCampaignGroup();
    const isDeleteEnabled = useConfigMatch({ [FEATURE_FLAGS.CAMPAIGN_GROUP_ENABLED]: 'true' });
    const isDuplicateEnabled = useConfigMatch({ [FEATURE_FLAGS.CAMPAIGN_GROUP_DUPLICATE_ENABLED]: 'true' });

    const buttonConfigs = [
        {
            onClick: () => openCampaignsGroupEditor(data),
            isDisabled,
            dataMetricComponent: 'EditActionButton',
            icon: <EditOutlinedIcon />,
            iconLabel: 'edit',
            tooltipContent: <CampaignsGroupEditTooltipContent isDisabled={isDisabled} />,
        },
    ];

    if (isDuplicateEnabled) {
        buttonConfigs.push({
            onClick: () => openCampaignsGroupDuplicate(data),
            isDisabled,
            dataMetricComponent: 'DuplicateActionButton',
            icon: <ContentCopyOutlinedIcon />,
            iconLabel: 'duplicate',
            tooltipContent: (
                <FormattedMessage id="app.campaigns.duplicate.campaign.group" defaultMessage="Duplicate Group" />
            ),
        });
    }

    if (isCampaignGroupExtensionEnabled && isDeleteEnabled) {
        buttonConfigs.push({
            onClick: () => {
                setIsDeleteDisabled(true);
                deleteCampaignGroup(data);
            },
            dataMetricComponent: 'DeleteActionButton',
            icon: <DeleteOutlineOutlinedIcon />,
            iconLabel: 'delete',
            tooltipContent: <FormattedMessage id="app.campaigns.delete.campaign.group" defaultMessage="Delete Group" />,
            isDisabled: isDisabled || isDeleteDisabled,
        });
    }

    return <ActionsCellRenderer data={data} buttonConfigs={buttonConfigs} context={context} {...rest} />;
};

CampaignsGroupActionsCellRenderer.propTypes = {
    data: PropTypes.object,
    onEdit: PropTypes.func,
    ...ActionsCellRenderer.propTypes,
};

export default CampaignsGroupActionsCellRenderer;
